import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Grid, Typography, IconButton, Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useContext, useEffect, useState } from 'react';
import {
  apiGetPlayer,
  PlaylistState
} from '../../store/playlistSlice';
import { OrganisationContext } from '../../App';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../index';
import { CreativeTagState, PlayerState } from '../types/playlist';
import ToolBarPlaylist from '../ToolBarPlaylist';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { AddPlaylistCreativeTagDialog } from '../modal/AddPlaylistCreativeTagDialog';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/env';
import removeButton from '../../icons/remove.png';
import { ReactComponent as PencilIcon } from '../../icons/pencil.svg';
import Table from '@mui/material/Table';
import { TableCellBody, TableCellHead, TableRow } from '../common/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import { Heading } from '../common/Heading';
import Link from '@mui/material/Link';
import { previewUrl } from '../utils/dspTag';

export default function PlaylistCreativeTags() {
  const { playlist, creativeId } = useParams();
  const dispatchRedux = useDispatch();
  const { selectedOrganisation } = useContext(OrganisationContext);
  const [creative, setCreative] = useState<PlayerState>();
  const navigate = useNavigate();
  const [copyTooltip, setCopyTooltip] = useState<string>('Copy to clipboard');

  const [tags, setTags] = useState<CreativeTagState[]>([]);

  const [tag, setTag] = useState<CreativeTagState | undefined>();

  const getTag = async () => {
    const { data } = await axios.get(`${getApiBaseUrl()}/playlist-tag/${selectedOrganisation}/${creativeId}`);
    setTags(data)
  }

  const {
    currentPlaylist,
    players,
  } = useSelector<RootState, PlaylistState>((state) => state.playlist);

  useEffect( () => {
    if (!selectedOrganisation) return;
    dispatchRedux(apiGetPlayer({
      publisher: selectedOrganisation,
      playlistId: playlist
    }));
    getTag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisation, playlist]);

  useEffect(() => {
    const creative = players.find(({ id }) => id === Number(creativeId));

    if (creative) setCreative(creative);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creativeId]);

  if (!currentPlaylist) {
    return <p>Loading..</p>;
  }

  const onEditTag = (tag: CreativeTagState) => {
    setTag(tag)
  }
  const onRemoveTag = async (tag: CreativeTagState) => {
    if (window.confirm(`Are you sure you want to remove '${tag.name}'?`)) {
      await axios.delete(`${getApiBaseUrl()}/playlist-tag/${selectedOrganisation}/${creativeId}/${tag.id}`);
      await getTag();
    }
  }

  return (
    <Box>
      <ToolBarPlaylist playlist={currentPlaylist} creative={creative}/>
      <Grid item xs={12}>
        <Grid container justifyContent={'space-between'}>
          <Grid item>
            <Heading>Tags</Heading>
          </Grid>
          <Grid item>
            <Button color="primary" variant="contained"
                    onClick={() => setTag({
                      playlist_player_id: creative?.id as number,
                      name: '',
                      tag: '',
                      dsp_name: ''
                    })}
                    startIcon={<AddCircleIcon/>}
                    type={'button'}>Add Tag</Button>
          </Grid>
        </Grid>
      </Grid>

      {tags.length > 0 && (
        <Table size="small" sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCellHead>NAME</TableCellHead>
              <TableCellHead>DSP</TableCellHead>
              <TableCellHead>DEALS</TableCellHead>
              <TableCellHead>ACTION</TableCellHead>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.map((row) => (
              <TableRow key={row.id}>
                <TableCellBody>
                  <Grid container alignItems="center" spacing={1} wrap="nowrap">
                    <Grid item><Link href={previewUrl(creative as PlayerState, selectedOrganisation as string)} target="_blank">{row.name}</Link></Grid>
                    <Grid item>
                      <Tooltip title={copyTooltip}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (row.tag) {
                              navigator.clipboard.writeText(row.tag);
                              setCopyTooltip('Copied!');
                            } else {
                              setCopyTooltip('Clipboard empty, failed to copy!');
                              alert('Unable to create tag creative. DSP not found');
                            }
                          }}
                        >
                          <ContentCopyIcon fontSize="small"/>
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </TableCellBody>
                <TableCellBody>{row.dsp_name}</TableCellBody>
                <TableCellBody>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AttachMoneyIcon/>}
                    onClick={() => navigate(`/playlist/creative/${playlist}/${creativeId}/tag/${row.id}/deals`)}
                  >
                    Deals
                  </Button>
                </TableCellBody>
                <TableCellBody align="center">
                  <Grid container justifyContent={'flex-start'} spacing={2}>
                    <Grid item>
                      <PencilIcon style={{ cursor: 'pointer' }} onClick={() => onEditTag(row)}/>
                    </Grid>
                    <Grid item>
                      <span onClick={() => onRemoveTag(row)} style={{ cursor: 'pointer' }}>
                        <img width="20" src={removeButton} alt="Remove tag"/>
                      </span>
                    </Grid>
                  </Grid>
                </TableCellBody>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {tags.length === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12}><Typography>No tag found</Typography></Grid>
        </Grid>
      )}

      { tag && <AddPlaylistCreativeTagDialog creative={creative as PlayerState} open={Boolean(tag)} handleClose={() => {
        setTag(undefined);
        getTag();
      }} tag={tag} />}
    </Box>
  );
}
